/**
 * A multi-line text control, unwrapped like the rest of the form family.
 *
 * @module components/ui/Form/TextAreaField
 */

import React, { type ChangeEvent, useId } from "react";

import { Textarea } from "react-daisyui";

import { useEditLock, useLockedRefusal } from "../EditLock";
import { ReadOnlyValue } from "./ReadOnlyValue";

export const TEXT_AREA_SIZES = ["xs", "sm", "md", "lg"] as const;
export type TextAreaSize = (typeof TEXT_AREA_SIZES)[number];

/** Written out so Tailwind sees each utility. */
const AUTOSIZE_CLASS = "field-sizing-content max-h-[60vh] min-h-24 resize-y";

export interface TextAreaFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Keys the caller handles itself. The narrative boxes are backed by a shared
   * document whose undo is scoped to one client's own edits, so the browser's
   * undo has to be intercepted rather than left to restore a stale value.
   */
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  /**
   * Refuses edits but stays focusable, as `aria-disabled` plus `readOnly`:
   * a natively disabled control leaves the tab order and takes its reason with it.
   */
  disabled?: boolean;
  /** Why it is disabled: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  required?: boolean;
  readOnly?: boolean;
  /** Visible rows before it scrolls. */
  rows?: number;
  /**
   * Grows with its content, from about three rows up to 60vh and then
   * scrolls, instead of cutting a long paragraph off at `rows`.
   */
  autosize?: boolean;
  size?: TextAreaSize;
  className?: string;
  "data-testid"?: string;
  "aria-describedby"?: string;
  "aria-label"?: string;
  /** Set by `FormField` while it holds an error for this control. */
  "aria-invalid"?: boolean;
}

export function TextAreaField({
  id,
  value,
  onChange,
  onKeyDown = undefined,
  placeholder = undefined,
  disabled: ownDisabled = false,
  required = false,
  readOnly = false,
  rows = 3,
  autosize = false,
  size = "sm",
  className = "",
  "data-testid": testId,
  "aria-describedby": ariaDescribedby,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid = undefined,
  ...reasonProps
}: TextAreaFieldProps): React.ReactElement {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, so the control is not
  // remounted, and does not lose focus, as the reason comes and goes.
  const { disabled, offersReason, disabledReason } = useLockedRefusal(ownDisabled, reasonProps);
  const reasonId = useId();
  const { locked } = useEditLock();
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const describedBy =
    [ariaDescribedby, reason ? reasonId : undefined].filter(Boolean).join(" ") || undefined;
  if (locked) {
    return (
      <ReadOnlyValue
        id={id}
        value={value}
        data-testid={testId}
        aria-describedby={ariaDescribedby}
        aria-label={ariaLabel}
      />
    );
  }
  // daisyUI paints a refused control from `:disabled` only.
  const refused = disabled ? "cursor-not-allowed border-base-200 bg-base-200 text-muted" : "";

  const box = (
    <Textarea
      id={id}
      value={value}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
        if (!disabled) {
          onChange(event.target.value);
        }
      }}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      aria-disabled={disabled || undefined}
      required={required}
      readOnly={readOnly || disabled}
      rows={autosize ? undefined : rows}
      size={size}
      className={["w-full text-sm", autosize ? AUTOSIZE_CLASS : "", refused, className]
        .filter(Boolean)
        .join(" ")}
      data-testid={testId}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-required={required}
    />
  );

  if (!offersReason) {
    return box;
  }
  return (
    <div className={reason ? "tooltip block w-full" : "contents"} data-tip={reason}>
      {box}
      {reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </div>
  );
}

export default TextAreaField;
